import React, { useEffect, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import Hls from 'hls.js';

type Props = {
  hlsUrl: string;
  onTimeUpdate?: (seconds: number) => void;
  onEnded?: () => void;
  paused?: boolean;
};

export default function VideoPlayer({ hlsUrl, onTimeUpdate, onEnded, paused }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const isHls = /\.m3u8(\?|$)/i.test(hlsUrl);

    if (!isHls) {
      video.src = hlsUrl;
      const onLoaded = () => {
        if (!paused) video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', onLoaded);
      return () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeAttribute('src');
        video.load();
      };
    }

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!paused) video.play();
      });
      hlsRef.current = hls;
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      video.addEventListener('loadedmetadata', () => {
        if (!paused) video.play();
      });
    }
  }, [hlsUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (paused) video.pause();
    else video.play().catch(() => {});
  }, [paused]);

  useEffect(() => {
    if (!onTimeUpdate) return;
    const interval = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        onTimeUpdate(Math.floor(videoRef.current.currentTime));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [onTimeUpdate]);

  return (
    <View style={styles.container}>
      <video
        ref={videoRef}
        style={styles.video as any}
        onEnded={onEnded}
        playsInline
        autoPlay
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
  },
});
